import {
  fullstorySelectors,
  AppDispatch,
  AppBackendRequestActions,
  namedElementDetailView,
  SearchMetaAsyncRequestActions,
  SearchMetaRequestActions,
  searchMetaSelectors,
  NamedElementsRequestActions,
} from '@fullstory/redux';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  VolumeTimeRange,
  GetNamedElementDetailViewResponse,
  VolumeAggregateBy,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  duplicateNamedElementRecord,
  namedElementEditableDetails,
  suggestionToEditableDetails,
  placeholderNamedElementRecord,
} from '../utils';
import { NamedElement } from '@fullstory/proto/fs/services/search-settings';
import jstz from 'jstz';
import { SnoozeUpdate } from '@fullstory/dqm-ui';
import { useFlags } from '@fullstory/react-hooks';
import { getElementPropertiesByElement } from '@fullstory/events-ui';
import { useElementPropertyRules } from './useElementPropertyRules';
import { isEqual } from 'lodash';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { NamedElementEditableDetails } from '../types';

const { getElementPropsLoading } = searchMetaSelectors;

const duplicateDetails = (
  details: GetNamedElementDetailViewResponse.Details,
): GetNamedElementDetailViewResponse.Details => ({
  ...placeholderNamedElementRecord,
  namedElement: duplicateNamedElementRecord(details.namedElement!),
  // duplicating ignores these flags, since unintentional copying can cause unintended side effects for the user
  watched: false,
  ignoreDeadClicks: false,
  ignoreRageClicks: false,
  issues: [],
  issuesData: {
    issues: [],
  },
  silence: null,
});

type UseNamedElementDetailsViewArg = {
  namedElementId: string | undefined;
  timeRange?: VolumeTimeRange;
  duplicate?: boolean;
  initialElement?: Partial<NamedElement>;
  isSuggestion?: boolean;
};

const timeRangeToAggregation = {
  [VolumeTimeRange.DAY_30]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.DAY_7]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.HOUR_72]: VolumeAggregateBy.HOUR,
};

export function useEditableNamedElementDetailView({
  namedElementId,
  timeRange = VolumeTimeRange.DAY_30,
  duplicate,
  initialElement,
  isSuggestion,
}: UseNamedElementDetailsViewArg) {
  const flags = useFlags();
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);

  // activity monitoring comes from request data so that we can stay in sync with time range/issues
  const requestData = useSelector(namedElementDetailView);
  const requestedTimeRange = useRef<VolumeTimeRange>();
  // state to track element details
  const [details, setDetails] = useState<NamedElementEditableDetails | null>(null);
  const [initialDetails, setInitialDetails] = useState(details);
  const hasUnsavedChanges = useMemo(
    () => !isEqual(details, initialDetails),
    [details, initialDetails],
  );

  const existingId = useRef<string>(duplicate ? '' : namedElementId ?? '');
  useEffect(() => {
    existingId.current = duplicate ? '' : namedElementId ?? '';
  }, [duplicate, namedElementId]);

  const setSnooze = useCallback(
    (update: SnoozeUpdate) => {
      setDetails(prev => {
        if (!prev || !namedElementId) return null;
        const next = { ...prev };
        switch (update.type) {
          case 'untilTime':
            next.silence = { namedElementId, id: '', expiresAt: update.t };
            break;
          case 'untilActivityReturns':
            next.silence = { namedElementId, id: '', expiresAt: null };
            break;
          case 'unsnooze':
            next.silence = null;
        }
        return next;
      });
    },
    [namedElementId],
  );

  const timezone = jstz.determine().name();

  const loadDetails = useCallback(
    (namedElementId: string, updateDetails: boolean, duplicate?: boolean) => {
      requestedTimeRange.current = timeRange;
      const request = AppBackendRequestActions.getNamedElementDetailView({
        orgId,
        namedElementId,
        timeRange,
        volumeAggregateBy: timeRangeToAggregation[timeRange],
        timezone,
      });
      dispatch(request).then(data => {
        if (updateDetails) {
          const d = duplicate ? duplicateDetails(data.details!) : data.details;
          const details = d ? namedElementEditableDetails(d) : null;
          setInitialDetails(details);
          setDetails(details);
        }
      });
    },
    [dispatch, orgId, timeRange, timezone],
  );

  const loadSuggestedElementDetails = useCallback(
    (suggestedElementId: string) => {
      const request = NamedElementsRequestActions.getSuggestedElement({
        orgId,
        suggestedElementId,
      });
      dispatch(request).then(data => {
        const suggestion = data.suggestion?.suggestion?.suggestion;
        if (suggestion) {
          const details = suggestionToEditableDetails(suggestion);
          setInitialDetails(details);
          setDetails(details);
        }
      });
    },
    [dispatch, orgId, timeRange, timezone],
  );

  // effect that fires on mount and when element ID changes
  // this is the initial details load for a given element
  useEffect(() => {
    if (namedElementId) {
      if (isSuggestion) {
        loadSuggestedElementDetails(namedElementId);
      } else {
        loadDetails(namedElementId, true, duplicate);
      }
    } else {
      const d = {
        namedElement: placeholderNamedElementRecord(initialElement, webOnly),
        watched: false,
        ignoreRageClicks: false,
        ignoreDeadClicks: false,
        silence: null,
      };
      const details = namedElementEditableDetails(d);
      setInitialDetails(details);
      setDetails(details);
    }
  }, [duplicate, initialElement, namedElementId, webOnly]);

  // effect that fires when time range changes
  // this only matters when there is a named element id involved
  // this should not overwrite any dettails
  useEffect(() => {
    if (namedElementId && requestedTimeRange.current !== timeRange && !isSuggestion) {
      loadDetails(namedElementId, false, duplicate);
    }
  }, [timeRange]);

  const elementPropertiesLoading = useSelector(getElementPropsLoading);
  const propertiesByElement = useSelector(getElementPropertiesByElement);
  const presentElementConnections = !!namedElementId;

  useEffect(() => {
    dispatch(SearchMetaRequestActions.getHiddenVars(orgId));
  }, [orgId, dispatch]);

  const getConnections = useCallback(() => {
    if (presentElementConnections && namedElementId) {
      dispatch(
        SearchMetaAsyncRequestActions.getCustomSchemaConnections(orgId, {
          elements: [namedElementId],
        }),
      );
    }
  }, [namedElementId, presentElementConnections, orgId, dispatch]);

  useEffect(() => {
    getConnections();
  }, [getConnections]);

  const { propertiesLoading, propertiesFailed, properties } = useMemo(() => {
    if (!namedElementId) {
      return {
        propertiesLoading: false,
        propertiesFailed: false,
        properties: [],
      };
    }
    const properties = propertiesByElement.get(namedElementId);
    const propertiesLoading = elementPropertiesLoading.has(namedElementId);
    const propertiesFailed = !properties && !propertiesLoading;
    return {
      propertiesLoading,
      propertiesFailed,
      properties: properties ?? [],
    };
  }, [namedElementId, elementPropertiesLoading, propertiesByElement]);

  const { allElementPropertyRules } = useElementPropertyRules();
  const elementPropertyRules = useMemo(
    () =>
      namedElementId
        ? allElementPropertyRules.filter(
            rule =>
              rule.elementPropertyRule?.namedElementId === namedElementId ||
              rule.elementPropertyRule?.childNamedElementId === namedElementId,
          )
        : undefined,
    [namedElementId, allElementPropertyRules],
  );

  const setDetailsSafe = useCallback((updatedDetails: NamedElementEditableDetails | null) => {
    const updatedId: string =
      updatedDetails?.namedElement?.namedElement?.id ||
      updatedDetails?.namedElement?.namedElement?.suggestion?.id ||
      '';
    // HACK: only allow updates for callers with the right id state
    // because we can swap out configs and there are some underlying flows that may not respect certain lifecycles
    // we're going to require callers to go through this safe path
    // things like ai trim need to be fixed to not fire callbacks when they are no longer relevant
    if (existingId.current === updatedId) {
      setDetails(updatedDetails);
    }
  }, []);

  return {
    ...requestData,
    setDetails: setDetailsSafe,
    details,
    initialDetails,
    hasUnsavedChanges,
    savedApiName: requestData.data?.details?.namedElement?.namedElement?.apiName ?? '',
    setSnooze,
    // Non-editable details need to respond to changes in request state.
    // Therefore we store and return the request data separately from local details that can be changed.
    snoozed: requestData.data?.details?.silence,
    issues: requestData.data?.details?.issuesData?.issues || [],
    activityMonitoring: requestData.data?.activityMonitoring,
    activityMonitoringError: requestData.data?.activityMonitoringError,
    auditHistory: requestData.data?.auditHistory,
    objectReferences: requestData.data?.objectReferences,
    properties: presentElementConnections ? properties : undefined,
    propertiesLoading,
    propertiesFailed,
    retryFailedProperties: getConnections,
    elementPropertyRules,
  };
}
